/**
 * Mimu cart extras (v10)
 *
 *  1. Bundle progress bar in the cart drawer / cart page, driven by cart quantity.
 *  2. Shipping protection via a visible, pre-checked checkbox:
 *       - the checkbox (mimu-protection-panel.liquid) reflects the shopper's
 *         actual choice; nothing is added to the real cart until they click
 *         checkout, and only if the box is still checked at that moment
 *       - the theme's own #checkout button is relabelled "Checkout | $total",
 *         where total already includes the fee whenever the box is checked
 *     Reusing the theme's button means Shopify's real cart-form submission still
 *     runs; nothing here fakes a redirect to /checkout.
 *
 * v7 replaces the old auto-add-plus-decline-link pattern (protection always
 * added on the button's default path, with a small text link to remove it)
 * with the checkbox above. The button no longer says "Protected" — it just
 * says "Checkout", since the checkbox is now the visible indicator of whether
 * protection is included.
 *
 * v6 collapses the progress bar from 4 stops to 2 (1 bag / 2 bags), matching the
 * mirrored bar in sections/header.liquid: free shipping is the only remaining
 * perk, unlocked at 2 bags.
 *
 * v8 adds a third stop for the 4-bag free-gift tier, matching the mirrored bar
 * in sections/header.liquid. The third stop jumps straight to 4 bags — there is
 * no "3 bags" stop in between — so filling can't be a simple linear count;
 * each stop needs its own bag-count threshold checked independently.
 *
 * v9 fixes a real bug: Appstle's "buy 4, get 1 free" gift is a persistent cart
 * LINE ITEM whose discount is recalculated live from current cart quantity —
 * dropping back below 4 bags removes the discount but not the line. Confirmed
 * live: the coconut gift line still carries Appstle's own
 * properties._appstle_is_free_gift marker afterward, just with
 * final_line_price back at full price. Since that property only ever appears
 * on Appstle's own gift line (never on a line the shopper added themselves),
 * it is a safe, unambiguous signal to remove the line automatically the
 * moment it stops being free — no risk of deleting a bag someone paid for.
 *
 * v10:
 *   - checkout button now says "Proceed to checkout | $total" (was "Checkout").
 *   - new subtotal/savings row above the button: item count, struck original
 *     total, current total, and a gold "$X off" pill — sourced directly from
 *     cart.original_total_price and cart.total_price, both native Shopify
 *     cart.js fields (confirmed live rather than computed by hand here).
 */
(function () {
  var THRESHOLDS = [1, 2, 4];

  function pickMessage(n) {
    if (n >= 4) return '<b>Free shipping + a free bag</b> unlocked';
    if (n >= 2) return 'Free shipping unlocked — <b>' + (4 - n) + ' more</b> for a free bag';
    if (n >= 1) return 'Add <b>1 more bag</b> to unlock free shipping';
    return 'Add <b>2 bags</b> to unlock free shipping';
  }

  var busy = false;

  function $all(sel) { return Array.prototype.slice.call(document.querySelectorAll(sel)); }
  function panel() { return document.querySelector('[data-mimu-pp]'); }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  /** True Shopify keys are derived from variant + line-item properties, so
   *  Appstle's gift line can never merge into a shopper's own purchase of the
   *  same variant — it always carries this property, which no other line
   *  ever has. */
  function isOrphanedGift(item) {
    return !!(
      item.properties &&
      item.properties['_appstle_is_free_gift'] === 'true' &&
      item.final_line_price > 0
    );
  }

  /** Let other cart-aware components (the native drawer's own line-item list,
   *  the cart icon count) know the cart changed, the same way
   *  mimu-cart-quickadd-v2.js does — a raw fetch here never triggers their
   *  own re-render on its own. */
  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'mimu-cart-extras',
          data: { source: 'mimu-cart-extras' }
        }
      })
    );
  }

  /** Remove any orphaned gift lines, one at a time — /cart/change.js
   *  recalculates the whole cart per call, so firing several in parallel
   *  risks one response clobbering another's change. Resolves true if
   *  anything was removed, so the caller knows to re-fetch. */
  function removeOrphanedGifts(cart) {
    var orphans = (cart.items || []).filter(isOrphanedGift);
    if (!orphans.length) return Promise.resolve(false);

    return orphans.reduce(function (chain, item) {
      return chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      });
    }, Promise.resolve()).then(function () { return true; });
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function launchIds(root) {
    return (root.getAttribute('data-launch-variants') || '').split(',')
      .map(function (v) { return parseInt(v, 10); })
      .filter(function (v) { return !isNaN(v); });
  }

  function countBags(cart, ids) {
    var n = 0;
    (cart.items || []).forEach(function (item) {
      var isLaunch = ids.indexOf(item.variant_id) !== -1 ||
        (typeof item.handle === 'string' && item.handle.indexOf('matcha-collagen-latte-launch') !== -1);
      if (isLaunch) n += item.quantity;
    });
    return n;
  }

  function protectionLine(cart, variantId) {
    var found = null;
    (cart.items || []).forEach(function (i) { if (i.variant_id === variantId) found = i; });
    return found;
  }

  /** A free line means the gift landed. Appstle discounts it to zero; a native
   *  BXGY does the same. Either way the shopper's total shows no charge for it. */
  function hasFreeGift(cart) {
    return (cart.items || []).some(function (item) {
      return item.final_line_price === 0 ||
        (item.total_discount > 0 && item.total_discount >= item.original_line_price);
    });
  }

  function renderGiftWarning(cart, bags) {
    var missing = bags >= 4 && !hasFreeGift(cart);
    $all('[data-mimu-cx-giftwarn]').forEach(function (el) { el.hidden = !missing; });
  }

  function renderProgress(cart) {
    $all('[data-mimu-cx]').forEach(function (root) {
      var bags = countBags(cart, launchIds(root));
      renderGiftWarning(cart, bags);
      var wrap = root.querySelector('[data-mimu-cx-progress]');
      if (wrap) wrap.hidden = bags < 1;
      root.querySelectorAll('[data-mimu-cx-seg]').forEach(function (s, i) {
        s.classList.toggle('is-filled', bags >= THRESHOLDS[i]);
      });
      root.querySelectorAll('[data-mimu-cx-label]').forEach(function (l, i) {
        l.classList.toggle('is-reached', bags >= THRESHOLDS[i]);
      });
      var msg = root.querySelector('[data-mimu-cx-msg]');
      if (msg) msg.innerHTML = pickMessage(bags);
    });
  }

  /** Relabel the theme's checkout button with the subtotal, adding the fee
   *  only when the checkbox is currently checked. */
  function renderCheckout(cart) {
    var p = panel();
    if (!p) return;
    var variantId = parseInt(p.getAttribute('data-protection-variant'), 10);
    var fee = parseInt(p.getAttribute('data-protection-cents'), 10) || 0;
    var checkbox = p.querySelector('[data-mimu-pp-checkbox]');
    var wantsProtection = checkbox ? checkbox.checked : true;

    // A protection line already in the cart (e.g. from an earlier visit) is
    // excluded from the base total and re-added from the checkbox instead, so
    // the button always matches what the checkbox currently says rather than
    // double-counting or lagging behind it.
    var existingLine = protectionLine(cart, variantId);
    var baseTotal = cart.total_price - (existingLine ? existingLine.final_line_price : 0);
    var total = baseTotal + (wantsProtection ? fee : 0);

    // Empty cart: leave the theme's button exactly as it is.
    if (!cart.item_count) { p.hidden = true; return; }
    p.hidden = false;

    $all('#checkout .button-text, .cart__checkout-button .button-text').forEach(function (el) {
      el.textContent = 'Proceed to checkout | ' + money(total);
    });
  }

  /** Subtotal + savings row above the checkout button, from cart's own native
   *  original_total_price / total_price fields — no per-line math needed. */
  function renderSubtotal(cart) {
    var el = document.querySelector('[data-mimu-cx-subtotal]');
    if (!el) return;

    if (!cart.item_count) { el.hidden = true; return; }
    el.hidden = false;

    var countEl = el.querySelector('[data-mimu-cx-subtotal-count]');
    var wasEl = el.querySelector('[data-mimu-cx-subtotal-was]');
    var nowEl = el.querySelector('[data-mimu-cx-subtotal-now]');
    var offEl = el.querySelector('[data-mimu-cx-subtotal-off]');

    if (countEl) {
      countEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }
    if (nowEl) nowEl.textContent = money(cart.total_price);

    var savings = cart.original_total_price - cart.total_price;

    if (wasEl) {
      wasEl.textContent = money(cart.original_total_price);
      wasEl.hidden = savings <= 0;
    }
    if (offEl) {
      if (savings > 0) {
        offEl.textContent = money(savings) + ' off';
        offEl.hidden = false;
      } else {
        offEl.hidden = true;
      }
    }
  }

  function setBusy(on) {
    busy = on;
    var p = panel();
    if (p) p.classList.toggle('is-busy', on);
  }

  function submitCart() {
    var form = document.querySelector('form#cart-form, form[action*="/cart"]');
    var button = document.querySelector('#checkout, .cart__checkout-button');
    if (form && typeof form.requestSubmit === 'function' && button) form.requestSubmit(button);
    else if (form) form.submit();
    else window.location.href = '/checkout';
  }

  function withProtection(variantId) {
    setBusy(true);
    getCart().then(function (cart) {
      if (protectionLine(cart, variantId)) return null;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
    }).then(submitCart).catch(function () { setBusy(false); submitCart(); });
  }

  function withoutProtection(variantId) {
    setBusy(true);
    getCart().then(function (cart) {
      var line = protectionLine(cart, variantId);
      if (!line) return null;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      });
    }).then(submitCart).catch(function () { setBusy(false); submitCart(); });
  }

  function bind() {
    var p = panel();
    if (!p || p.hasAttribute('data-bound')) return;
    p.setAttribute('data-bound', '');
    var variantId = parseInt(p.getAttribute('data-protection-variant'), 10);
    var checkbox = p.querySelector('[data-mimu-pp-checkbox]');

    // Primary: intercept the theme's checkout button so the cart matches
    // whatever the checkbox currently says before the form submits.
    document.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('#checkout, .cart__checkout-button') : null;
      if (!btn || busy || !panel() || panel().hidden) return;
      event.preventDefault();
      if (checkbox && !checkbox.checked) withoutProtection(variantId);
      else withProtection(variantId);
    }, true);

    // Reflect a toggle in the displayed total right away, rather than waiting
    // for the next 2s poll in sync().
    if (checkbox) {
      checkbox.addEventListener('change', function () {
        getCart().then(renderCheckout).catch(function () {});
      });
    }
  }

  function sync() {
    bind();
    return getCart().then(function (cart) {
      return removeOrphanedGifts(cart).then(function (removed) {
        return removed ? getCart().then(function (fresh) { announce(fresh); return fresh; }) : cart;
      });
    }).then(function (cart) {
      renderProgress(cart);
      renderCheckout(cart);
      renderSubtotal(cart);
    }).catch(function () {});
  }

  document.addEventListener('cart:update', function () { setTimeout(sync, 60); });
  new MutationObserver(function () { bind(); }).observe(document.body, { childList: true, subtree: true });
  setInterval(sync, 2000);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
